import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useCurrency } from './CurrencyContext';

const PaymentContext = createContext();

export function usePayment() {
  return useContext(PaymentContext);
}

export function PaymentProvider({ children }) {
  const { currentUser, apiCall } = useAuth();
  const { currencyCode } = useCurrency();
  const [commissionRates, setCommissionRates] = useState({
    jobPosting: 5, // 5% commission on job postings
    serviceBooking: 10, // 10% commission on service bookings
    premiumSubscription: 15, // Fixed amount for premium subscriptions
  });
  const [transactions, setTransactions] = useState([]);

  // Derived safe rates to avoid reading properties of undefined
  const jobPostingRate = Number(commissionRates?.jobPosting ?? 5);
  const serviceBookingRate = Number(commissionRates?.serviceBooking ?? 10);
  const premiumRate = Number(commissionRates?.premiumSubscription ?? 15);

  // Fetch commission rates from API
  useEffect(() => {
    const fetchCommissionRates = async () => {
      try {
        const response = await apiCall('/public/commission-rates');
        if (response.success) {
          setCommissionRates(response.rates);
        }
      } catch (error) {
        console.error('Error fetching commission rates:', error);
      }
    };
    fetchCommissionRates();
  }, [apiCall]);

  // Update commission rates (admin only)
  const updateCommissionRates = useCallback(async (newRates) => {
    try {
      const response = await apiCall('/admin/commission-rates', {
        method: 'PUT',
        body: JSON.stringify(newRates),
      });
      
      if (response.success) {
        setCommissionRates(newRates);
        return true;
      }
      
      throw new Error(response.message || 'Failed to update commission rates');
    } catch (error) {
      console.error('Error updating commission rates:', error);
      throw error;
    }
  }, [apiCall]);

  // Process job posting payment
  const processJobPostingPayment = useCallback(async (jobData, amount) => {
    try {
      const commission = (amount * jobPostingRate) / 100;
      const transaction = {
        type: 'jobPosting',
        employerId: currentUser?._id,
        jobId: jobData.id,
        amount,
        commission,
        status: 'completed',
        timestamp: new Date().toISOString(),
      };
      
      const response = await apiCall('/payments/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction),
      });
      
      if (response.success) {
        return response.transaction;
      }
      
      throw new Error(response.message || 'Payment processing failed');
    } catch (error) {
      console.error('Error processing job posting payment:', error);
      throw error;
    }
  }, [jobPostingRate, currentUser?._id, apiCall]);

  // Process service booking payment
  const processServiceBookingPayment = useCallback(async (serviceData, amount) => {
    try {
      const commission = (amount * serviceBookingRate) / 100;
      const transaction = {
        type: 'serviceBooking',
        providerId: serviceData.providerId,
        seekerId: currentUser?._id,
        serviceId: serviceData.id,
        amount,
        commission,
        status: 'completed',
        timestamp: new Date().toISOString(),
      };
      
      const response = await apiCall('/payments/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction),
      });
      
      if (response.success) {
        return response.transaction;
      }
      
      throw new Error(response.message || 'Payment processing failed');
    } catch (error) {
      console.error('Error processing service booking payment:', error);
      throw error;
    }
  }, [serviceBookingRate, currentUser?._id, apiCall]);

  // Process premium subscription payment
  const processPremiumSubscription = useCallback(async (userId, subscriptionType) => {
    try {
      const amount = premiumRate;
      const transaction = {
        type: 'premiumSubscription',
        userId,
        subscriptionType,
        amount,
        status: 'completed',
        timestamp: new Date().toISOString(),
      };
      
      const response = await apiCall('/payments/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction),
      });
      
      if (response.success) {
        return response.transaction;
      }
      
      throw new Error(response.message || 'Payment processing failed');
    } catch (error) {
      console.error('Error processing premium subscription:', error);
      throw error;
    }
  }, [premiumRate, apiCall]);

  // Process profile boost payment with optional baseAmount, durationDays, and payment details
  const processProfileBoostPayment = useCallback(async (userId, baseAmount, durationDays, paymentDetails = {}) => {
    try {
      // If a base amount is provided (from system settings), use it; otherwise fall back
      const amount = typeof baseAmount === 'number' && baseAmount > 0
        ? baseAmount
        : premiumRate;
      // If MTN MoMo mobile money is selected, initiate a real collection and wait
      const isMoMo = (paymentDetails?.paymentMethod === 'mobile_money') && (paymentDetails?.mobileOperator === 'MTN');
      if (isMoMo) {
        // Normalize MSISDN for Zambia: ensure starts with 260...
        let msisdn = String(paymentDetails?.mobileNumber || '').replace(/\s+/g, '');
        if (msisdn.startsWith('+')) msisdn = msisdn.slice(1);
        if (/^0\d{9}$/.test(msisdn)) msisdn = `260${msisdn.slice(1)}`;

        const chargeResp = await apiCall('/payments/momo/charge', {
          method: 'POST',
          body: JSON.stringify({
            amount,
            currency: currencyCode || 'ZMW',
            mobileNumber: msisdn,
            message: 'JobIt Profile Boost',
            payeeNote: 'JobIt',
            externalId: `profile_boost_${userId}_${Date.now()}`,
          }),
        });

        if (!chargeResp?.success) {
          throw new Error(chargeResp?.message || 'Mobile money payment failed to start');
        }

        if (chargeResp.status !== 'SUCCESSFUL') {
          const timeoutMsg = chargeResp?.timeout ? ' Payment pending. Please complete on your phone.' : '';
          throw new Error(`Payment not successful: ${chargeResp.status}.${timeoutMsg}`);
        }
      }
      const transaction = {
        type: 'profile_boost',
        userId,
        amount,
        durationDays: Number(durationDays) || 1,
        status: 'completed',
        timestamp: new Date().toISOString(),
        // include payment details for admin visibility
        paymentMethod: paymentDetails.paymentMethod || 'mobile',
        mobileOperator: paymentDetails.mobileOperator,
        mobileNumber: paymentDetails.mobileNumber,
      };

      const response = await apiCall('/payments/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction),
      });

      if (response.success) {
        return response.transaction;
      }

      throw new Error(response.message || 'Profile boost payment failed');
    } catch (error) {
      console.error('Error processing profile boost payment:', error);
      throw error;
    }
  }, [currencyCode, premiumRate, apiCall]);

  // Process job boost payment
  const processJobBoostPayment = useCallback(async (jobId, employerId, baseAmount) => {
    try {
      const commission = (baseAmount * jobPostingRate) / 100;
      const transaction = {
        type: 'job_boost',
        employerId,
        jobId,
        amount: baseAmount + commission,
        commission,
        status: 'completed',
        timestamp: new Date().toISOString(),
      };

      const response = await apiCall('/payments/transactions', {
        method: 'POST',
        body: JSON.stringify(transaction),
      });

      if (response.success) {
        return response.transaction;
      }

      throw new Error(response.message || 'Job boost payment failed');
    } catch (error) {
      console.error('Error processing job boost payment:', error);
      throw error;
    }
  }, [jobPostingRate, apiCall]);

  // Get all transactions (admin only)
  const getAllTransactions = useCallback(async () => {
    try {
      const response = await apiCall('/payments/transactions');
      
      if (response.success) {
        setTransactions(response.transactions);
        return response.transactions;
      }
      
      throw new Error(response.message || 'Failed to fetch transactions');
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }, [apiCall]);

  // Get user transactions
  const getUserTransactions = useCallback(async (userId) => {
    try {
      const response = await apiCall(`/payments/transactions/user/${userId}`);
      
      if (response.success) {
        return response.transactions;
      }
      
      throw new Error(response.message || 'Failed to fetch user transactions');
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      throw error;
    }
  }, [apiCall]);

  const value = useMemo(() => ({
    commissionRates,
    transactions,
    updateCommissionRates,
    processJobPostingPayment,
    processServiceBookingPayment,
    processPremiumSubscription,
    processProfileBoostPayment,
    processJobBoostPayment,
    getAllTransactions,
    getUserTransactions,
  }), [
    commissionRates,
    transactions,
    updateCommissionRates,
    processJobPostingPayment,
    processServiceBookingPayment,
    processPremiumSubscription,
    processProfileBoostPayment,
    processJobBoostPayment,
    getAllTransactions,
    getUserTransactions,
  ]);

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
}